import { useState } from 'react';
import CreatorSidebar from '../../components/feature/CreatorSidebar';
import UploadModal from '../../components/feature/UploadModal';

interface Subscriber {
  id: number;
  name: string;
  avatar: string;
  level: string;
  levelColor: string;
  subscribedDate: string;
  totalTips: number;
}

interface TipRequest {
  id: number;
  subscribers: string[];
  message: string;
  amount: number;
  createdAt: string;
  responses: number;
  totalReceived: number;
}

export default function Tips() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedSubscribers, setSelectedSubscribers] = useState<number[]>([]);
  const [tipMessage, setTipMessage] = useState('');
  const [suggestedAmount, setSuggestedAmount] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');

  // Mock data - 구독자 목록
  const subscribers: Subscriber[] = [
    {
      id: 1,
      name: '민수',
      avatar: 'https://public.readdy.ai/ai/img_res/417111f3f6638d05352e523a098cf6a7.jpg',
      level: '다이아몬드',
      levelColor: 'from-purple-400 to-pink-400',
      subscribedDate: '2024-01-15',
      totalTips: 450
    },
    {
      id: 2,
      name: '지수',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20fan%20profile%20photo%20sweet%20smile%20casual%20style%20clean%20simple%20background&width=100&height=100&seq=sub2&orientation=squarish',
      level: 'VIP',
      levelColor: 'from-yellow-400 to-orange-400',
      subscribedDate: '2024-02-20',
      totalTips: 320
    },
    {
      id: 3,
      name: '현우',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20male%20fan%20profile%20photo%20bright%20smile%20casual%20style%20clean%20simple%20background&width=100&height=100&seq=sub3&orientation=squarish',
      level: '프리미엄',
      levelColor: 'from-blue-400 to-cyan-400',
      subscribedDate: '2024-03-10',
      totalTips: 180
    },
    {
      id: 4,
      name: '서연',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20fan%20profile%20photo%20cheerful%20smile%20casual%20style%20clean%20simple%20background&width=100&height=100&seq=sub4&orientation=squarish',
      level: '스탠다드',
      levelColor: 'from-green-400 to-emerald-400',
      subscribedDate: '2024-03-25',
      totalTips: 90
    },
    {
      id: 5,
      name: '준호',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20male%20fan%20profile%20photo%20warm%20smile%20casual%20style%20clean%20simple%20background&width=100&height=100&seq=sub5&orientation=squarish',
      level: '베이직',
      levelColor: 'from-gray-400 to-slate-400',
      subscribedDate: '2024-04-01',
      totalTips: 30
    },
    {
      id: 6,
      name: '유진',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20fan%20profile%20photo%20lovely%20smile%20casual%20style%20clean%20simple%20background&width=100&height=100&seq=sub6&orientation=squarish',
      level: '다이아몬드',
      levelColor: 'from-purple-400 to-pink-400',
      subscribedDate: '2024-01-20',
      totalTips: 520
    },
    {
      id: 7,
      name: '태양',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20male%20fan%20profile%20photo%20happy%20smile%20casual%20style%20clean%20simple%20background&width=100&height=100&seq=sub7&orientation=squarish',
      level: 'VIP',
      levelColor: 'from-yellow-400 to-orange-400',
      subscribedDate: '2024-02-15',
      totalTips: 280
    },
    {
      id: 8,
      name: '하은',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20fan%20profile%20photo%20gentle%20smile%20casual%20style%20clean%20simple%20background&width=100&height=100&seq=sub8&orientation=squarish',
      level: '프리미엄',
      levelColor: 'from-blue-400 to-cyan-400',
      subscribedDate: '2024-03-05',
      totalTips: 150
    }
  ];

  // Mock data - 팁 요청 내역
  const tipRequests: TipRequest[] = [
    {
      id: 1,
      subscribers: ['민수', '지수', '현우', '서연'],
      message: '새로운 콘텐츠 제작을 위해 응원 부탁드립니다! 💕',
      amount: 50,
      createdAt: '2024-04-15 14:30',
      responses: 3,
      totalReceived: 150
    },
    {
      id: 2,
      subscribers: ['유진', '태양'],
      message: '특별한 화보 촬영을 준비 중입니다! 🎬',
      amount: 100,
      createdAt: '2024-04-10 10:20',
      responses: 2,
      totalReceived: 200
    },
    {
      id: 3,
      subscribers: ['민수', '유진', '태양', '지수', '하은'],
      message: '여러분의 응원이 큰 힘이 됩니다! ✨',
      amount: 30,
      createdAt: '2024-04-05 16:45',
      responses: 4,
      totalReceived: 180
    }
  ];

  const handleSelectAll = () => {
    const filtered = getFilteredSubscribers();
    if (selectedSubscribers.length === filtered.length) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(filtered.map(s => s.id));
    }
  };

  const handleSelectSubscriber = (id: number) => {
    if (selectedSubscribers.includes(id)) {
      setSelectedSubscribers(selectedSubscribers.filter(sid => sid !== id));
    } else {
      setSelectedSubscribers([...selectedSubscribers, id]);
    }
  };

  const getFilteredSubscribers = () => {
    return subscribers.filter(sub => {
      const matchesSearch = sub.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesLevel = filterLevel === 'all' || sub.level === filterLevel;
      return matchesSearch && matchesLevel;
    });
  };

  const handleSendRequest = () => {
    if (selectedSubscribers.length === 0) {
      alert('최소 1명 이상의 구독자를 선택해주세요.');
      return;
    }
    if (!tipMessage.trim()) {
      alert('팁 요청 메시지를 입력해주세요.');
      return;
    }
    if (!suggestedAmount || Number(suggestedAmount) < 1) {
      alert('권장 금액을 입력해주세요. (최소 1피치)');
      return;
    }

    alert(`${selectedSubscribers.length}명의 구독자에게 팁 요청을 보냈습니다! 🍑`);
    setShowRequestModal(false);
    setSelectedSubscribers([]);
    setTipMessage('');
    setSuggestedAmount('');
  };

  const filteredSubscribers = getFilteredSubscribers();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-pink-50 to-purple-50">
      <CreatorSidebar onUploadClick={() => setShowUploadModal(true)} />
      
      <div className="lg:ml-64 min-h-screen">
        {/* Top Bar */}
        <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-pink-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">팁 받기</h1>
              <p className="text-sm text-gray-600 mt-1">구독자에게 팁을 요청하고 관리하세요</p>
            </div>
            <button
              onClick={() => setShowRequestModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-gift-line mr-2"></i>
              팁 요청하기
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-pink-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">총 구독자</span>
                <i className="ri-user-heart-line text-2xl text-pink-500"></i>
              </div>
              <p className="text-3xl font-bold text-gray-800">{subscribers.length}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">이번 달 팁</span>
                <i className="ri-gift-line text-2xl text-purple-500"></i>
              </div>
              <p className="text-3xl font-bold text-gray-800">1,520🍑</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">팁 요청</span>
                <i className="ri-send-plane-line text-2xl text-blue-500"></i>
              </div>
              <p className="text-3xl font-bold text-gray-800">{tipRequests.length}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">응답률</span>
                <i className="ri-percent-line text-2xl text-green-500"></i>
              </div>
              <p className="text-3xl font-bold text-gray-800">75%</p>
            </div>
          </div>

          {/* Tip Requests History */}
          <div className="bg-white rounded-2xl shadow-sm border border-pink-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-800">팁 요청 내역</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">날짜</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">메시지</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">대상</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">권장 금액</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">응답</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">받은 금액</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tipRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-pink-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-600">{request.createdAt}</td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-800 line-clamp-1">{request.message}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-700">{request.subscribers.length}명</span>
                          <div className="group relative">
                            <i className="ri-information-line text-gray-400 cursor-pointer"></i>
                            <div className="hidden group-hover:block absolute left-0 top-6 bg-gray-800 text-white text-xs rounded-lg p-2 whitespace-nowrap z-10">
                              {request.subscribers.join(', ')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-gray-800">{request.amount}🍑</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">
                          {request.responses}/{request.subscribers.length}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-pink-600">{request.totalReceived}🍑</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Tip Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">팁 요청하기</h2>
                <p className="text-sm text-gray-600 mt-1">구독자를 선택하고 메시지를 작성하세요</p>
              </div>
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setSelectedSubscribers([]);
                  setTipMessage('');
                  setSuggestedAmount('');
                }}
                className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Message Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <i className="ri-message-3-line mr-2"></i>
                  팁 요청 메시지
                </label>
                <textarea
                  value={tipMessage}
                  onChange={(e) => setTipMessage(e.target.value)}
                  placeholder="구독자들에게 전할 메시지를 입력하세요... (예: 새로운 콘텐츠 제작을 위해 응원 부탁드립니다!)"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:outline-none resize-none"
                  rows={4}
                  maxLength={300}
                />
                <p className="text-xs text-gray-500 mt-2">{tipMessage.length}/300</p>
              </div>

              {/* Suggested Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <i className="ri-gift-line mr-2"></i>
                  권장 팁 금액
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={suggestedAmount}
                    onChange={(e) => setSuggestedAmount(e.target.value)}
                    placeholder="권장 금액 입력 (최소 1피치)"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:outline-none"
                    min="1"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">🍑</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">구독자들에게 제안할 팁 금액입니다. (강제가 아닙니다)</p>
              </div>

              {/* Subscriber Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">
                    <i className="ri-user-heart-line mr-2"></i>
                    구독자 선택 ({selectedSubscribers.length}명 선택됨)
                  </label>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-pink-600 hover:text-pink-700 font-semibold cursor-pointer"
                  >
                    {selectedSubscribers.length === filteredSubscribers.length ? '전체 해제' : '전체 선택'}
                  </button>
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="flex-1 relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="구독자 검색..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:border-pink-500 focus:outline-none text-sm"
                    />
                  </div>
                  <select
                    value={filterLevel}
                    onChange={(e) => setFilterLevel(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-xl focus:border-pink-500 focus:outline-none text-sm cursor-pointer"
                  >
                    <option value="all">모든 레벨</option>
                    <option value="다이아몬드">다이아몬드</option>
                    <option value="VIP">VIP</option>
                    <option value="프리미엄">프리미엄</option>
                    <option value="스탠다드">스탠다드</option>
                    <option value="베이직">베이직</option>
                  </select>
                </div>

                {/* Subscriber List */}
                <div className="border-2 border-gray-200 rounded-xl max-h-80 overflow-y-auto">
                  {filteredSubscribers.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <i className="ri-user-search-line text-4xl mb-2"></i>
                      <p>검색 결과가 없습니다</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filteredSubscribers.map((subscriber) => (
                        <div
                          key={subscriber.id}
                          onClick={() => handleSelectSubscriber(subscriber.id)}
                          className={`p-4 flex items-center gap-4 cursor-pointer transition-colors ${
                            selectedSubscribers.includes(subscriber.id)
                              ? 'bg-gradient-to-r from-pink-50 to-purple-50'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div
                            className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 ${
                              selectedSubscribers.includes(subscriber.id)
                                ? 'border-pink-500 bg-pink-500'
                                : 'border-gray-300'
                            }`}
                          >
                            {selectedSubscribers.includes(subscriber.id) && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                            <img
                              src={subscriber.avatar}
                              alt={subscriber.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-800">{subscriber.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r ${subscriber.levelColor} text-white text-xs font-semibold`}>
                                <i className="ri-vip-crown-fill"></i>
                                {subscriber.level}
                              </span>
                              <span className="text-xs text-gray-500">총 {subscriber.totalTips}🍑 후원</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-800">{selectedSubscribers.length}명</span>의 구독자에게 팁을 요청합니다
                </div>
                {suggestedAmount && (
                  <div className="text-sm text-gray-600">
                    권장 금액: <span className="font-bold text-pink-600">{suggestedAmount}🍑</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleSendRequest}
                disabled={selectedSubscribers.length === 0 || !tipMessage.trim() || !suggestedAmount}
                className="w-full py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-fill mr-2"></i>
                팁 요청 보내기
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && <UploadModal onClose={() => setShowUploadModal(false)} />}
    </div>
  );
}
